import { ReactNode } from 'react';

import IconAddPlus from '../../../icons/IconAddPlus.svg';
import { Button } from '../../../shared/Button';

import styles from './ArrayField.module.scss';

export interface ArrayFieldItemProps {
    index: number;
    title?: string;
    canMoveUp: boolean;
    canMoveDown: boolean;
    canRemove: boolean;
    onRemove: () => void;
    onReorder: (fromIndex: number, toIndex: number) => void;
    children?: ReactNode;
    direction?: 'vertical' | 'horizontal';
    readOnly?: boolean;
}

export const ArrayFieldItem = ({
    index,
    onRemove,
    onReorder,
    canRemove,
    canMoveDown,
    canMoveUp,
    children,
    title,
    direction,
    readOnly = false,
}: ArrayFieldItemProps) => {
    const onMoveUp = () => onReorder(index, index - 1);
    const onMoveDown = () => onReorder(index, index + 1);
    return (
        <fieldset>
            <legend className={styles.legendContainer}>
                <p>{title ?? `Item ${index}`}</p>
                {!readOnly && (
                    <>
                        <Button
                            disabled={!canRemove}
                            color="danger"
                            size="small"
                            onClick={onRemove}
                        >
                            X
                        </Button>
                        <Button
                            disabled={!canMoveUp}
                            color="secondary"
                            size="small"
                            onClick={onMoveUp}
                        >
                            {direction === 'horizontal' ? '⬅️' : '⬆️'}
                        </Button>
                        <Button
                            disabled={!canMoveDown}
                            color="secondary"
                            size="small"
                            onClick={onMoveDown}
                        >
                            {direction === 'horizontal' ? '➡️' : '⬇️'}
                        </Button>
                    </>
                )}
            </legend>
            <div
                className={
                    direction === 'horizontal'
                        ? styles.horizontalItemContent
                        : styles.verticalItemContent
                }
            >
                {children}
            </div>
        </fieldset>
    );
};

export interface ArrayFieldProps<T> {
    onChange?: (items: Array<T>) => void;
    onAdd?: () => void;
    items: Array<T>;
    children: (item: {
        item: T;
        index: number;
        canMoveUp: boolean;
        canMoveDown: boolean;
        canRemove: boolean;
        onChange: (value: T) => void;
        onRemove: () => void;
        onReorder: (fromIndex: number, toIndex: number) => void;
        direction?: 'vertical' | 'horizontal';
        readOnly?: boolean;
    }) => ReactNode;
    minItems?: number;
    maxItems?: number;
    direction?: 'vertical' | 'horizontal';
    readOnly?: boolean;
}

export const ArrayField = <T,>({
    items,
    children,
    onChange,
    onAdd,
    minItems,
    maxItems,
    direction = 'vertical',
    readOnly = false,
}: ArrayFieldProps<T>) => {
    const canAdd = maxItems === undefined || items.length < maxItems;
    const canRemove = minItems === undefined || items.length > minItems;

    return (
        <div>
            <div
                className={
                    direction === 'horizontal'
                        ? styles.horizontalContainer
                        : styles.verticalContainer
                }
            >
                {items.map((item, index) => {
                    const onItemChange = (item: T) => {
                        const newItems = [
                            ...items.slice(0, index),
                            item,
                            ...items.slice(index + 1),
                        ];
                        onChange?.(newItems);
                    };
                    const onRemove = () => {
                        onChange?.(items.filter((_, i) => i !== index));
                    };
                    const onReorder = (fromIndex: number, toIndex: number) => {
                        const newItems = [...items];
                        const moved = newItems.splice(fromIndex, 1)[0];
                        newItems.splice(toIndex, 0, moved);
                        onChange?.(newItems);
                    };

                    const canMoveUp = index > 0;
                    const canMoveDown = index < items.length - 1;

                    return children({
                        item,
                        index,
                        canMoveUp,
                        canMoveDown,
                        onChange: onItemChange,
                        onRemove,
                        onReorder,
                        canRemove,
                        direction,
                        readOnly,
                    });
                })}
            </div>
            {canAdd && !readOnly && (
                <Button
                    type="button"
                    onClick={onAdd}
                    startContent={<img src={IconAddPlus} alt="Add" />}
                >
                    Add New
                </Button>
            )}
        </div>
    );
};
